/**
 * src/walletConnectReadiness.ts
 *
 * Deterministic readiness for the wallets-kit WalletConnect module.
 *
 * WHY THIS EXISTS
 * ---------------
 * `WalletConnectModule`'s constructor starts its relay handshake and throws the
 * promise away:
 *
 *     SignClient.init({ ... }).then((client) => { this.signClient = client; });
 *
 * There is no `await`, no readiness promise, and (as of 2.6.0) not even a
 * `.catch()`. Every method then calls `runChecks()`, which throws
 * "WalletConnect modules has not been started yet." while `signClient` is
 * undefined. On a phone the handshake is slow enough that the first one or two
 * taps land inside that window, which is the "Connection Failed" loop users hit.
 *
 * This module turns that into something callers can await, without a timer,
 * without polling, and without retrying.
 *
 * WHAT IT DEPENDS ON
 * ------------------
 * `signClient` is a declared public property on `WalletConnectModule` (it is in
 * the kit's own .d.ts, not a private field), and assignment to it is the exact
 * moment the module becomes usable. We install an accessor over that property
 * so the kit's own assignment resolves our promise. That is the real lifecycle
 * signal, not an approximation of one.
 *
 * Failure is harder. Because the kit never attaches a `.catch()`, a rejected
 * `SignClient.init()` surfaces only as a global `unhandledrejection`. We listen
 * for that while, and only while, readiness is still pending and no client has
 * arrived. It is a heuristic - an unrelated rejection inside that short window
 * would be misattributed - but the alternative is leaving the caller waiting
 * forever, and the failure mode we chose is an honest error instead of a hang.
 *
 * REMOVAL PATH
 * ------------
 * If a future wallets-kit exposes real readiness (an awaitable init, a ready
 * promise, or a lifecycle event), delete this file and await that instead. The
 * only integration point is `observeWalletConnectReadiness` in src/wallet.ts.
 */

import type { WalletConnectModule } from "@creit.tech/stellar-wallets-kit/modules/wallet-connect";

export interface WalletConnectReadiness {
  /**
   * Settles when the module's sign client is live, or rejects if initialization
   * failed. Never pends indefinitely on a detectable failure.
   */
  readonly ready: Promise<void>;
  /** Mirrors the kit's own `isAvailable()`: can this module serve a request now? */
  isReady(): boolean;
  /** Stop observing and put the module's property back the way we found it. */
  dispose(): void;
}

/** Already-settled readiness, for when WalletConnect is not configured at all. */
export function noWalletConnect(): WalletConnectReadiness {
  return {
    ready: Promise.resolve(),
    isReady: () => false,
    dispose: () => {},
  };
}

export function observeWalletConnectReadiness(
  wc: WalletConnectModule,
): WalletConnectReadiness {
  let settled = false;
  let resolveReady!: () => void;
  let rejectReady!: (error: Error) => void;

  const ready = new Promise<void>((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });
  // Callers may never await `ready` (WalletConnect is optional). Keep a rejection
  // from becoming an unhandled one in its own right.
  ready.catch(() => {});

  const isReady = () => !!wc.signClient && !!wc.modal;

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    // A client that arrived first wins; this rejection belongs to something else.
    if (settled || wc.signClient) return;
    const reason = (event.reason as Error | undefined)?.message || String(event.reason);
    settle(new Error(`WalletConnect could not start: ${reason}`));
  };

  function stopListening() {
    if (typeof window !== "undefined") {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    }
  }

  function settle(error?: Error) {
    if (settled) return;
    settled = true;
    stopListening();
    if (error) rejectReady(error);
    else resolveReady();
  }

  // The client may already be in place if the module was built earlier.
  if (wc.signClient) {
    settled = true;
    resolveReady();
    return {
      ready,
      isReady,
      dispose: () => {},
    };
  }

  // Observe the assignment itself. The accessor is transparent: reads and writes
  // behave exactly as they did on the plain data property, so the kit cannot
  // tell the difference.
  // Held as `unknown`: the kit types this property via a `default as Client`
  // re-import that does not resolve here, and we only ever check it for presence
  // and hand it straight back.
  let client: unknown = wc.signClient;
  Object.defineProperty(wc, "signClient", {
    configurable: true,
    enumerable: true,
    get: () => client,
    set: (next: unknown) => {
      client = next;
      if (next) settle();
    },
  });

  if (typeof window !== "undefined") {
    window.addEventListener("unhandledrejection", onUnhandledRejection);
  }

  return {
    ready,
    isReady,
    dispose: () => {
      stopListening();
      // Restore a plain data property holding whatever the kit last wrote.
      Object.defineProperty(wc, "signClient", {
        configurable: true,
        enumerable: true,
        writable: true,
        value: client,
      });
    },
  };
}
